import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Eye, Loader2, Image as ImageIcon } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'link'];

export default function ArticleEditor({ article, onClose, onSaved }) {
  const [title, setTitle] = useState(article.title || '');
  const [excerpt, setExcerpt] = useState(article.excerpt || '');
  const [content, setContent] = useState(article.content || '');
  const [category, setCategory] = useState(article.category || '');
  const [tags, setTags] = useState((article.tags || []).join(', '));
  const [status, setStatus] = useState(article.status || 'draft');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const token = localStorage.getItem('token');
      const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);
      await axios.put(`${API}/api/blog/admin/articles/${article.id}`, {
        title, excerpt, content, category, tags: tagList, status,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMsg('Artikel berhasil disimpan!');
      if (onSaved) onSaved();
    } catch (err) {
      setMsg('Gagal menyimpan: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(8px)',
    }} data-testid="article-editor-modal">
      <div style={{
        background: '#141414', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem',
        width: '95%', maxWidth: '900px', maxHeight: '95vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <h3 style={{ color: '#EDEDED', fontSize: '1rem', margin: 0 }}>Edit Artikel</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={() => setPreview(!preview)} data-testid="editor-preview-toggle" style={{
              background: preview ? '#d9fb06' : 'rgba(255,255,255,0.08)', color: preview ? '#0A0A0A' : '#A1A1AA',
              border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '0.8rem',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}><Eye size={14} /> {preview ? 'Editor' : 'Preview'}</button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#71717A', cursor: 'pointer' }}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {!preview ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelStyle}>Judul</label>
                <input data-testid="editor-title" value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Excerpt / Ringkasan</label>
                <textarea data-testid="editor-excerpt" value={excerpt} onChange={e => setExcerpt(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Kategori</label>
                  <input data-testid="editor-category" value={category} onChange={e => setCategory(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <select data-testid="editor-status" value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Tags (pisahkan dengan koma)</label>
                <input data-testid="editor-tags" value={tags} onChange={e => setTags(e.target.value)} placeholder="behavioral finance, wealth logic, ..." style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Konten</label>
                <div className="quill-editor-wrapper" style={{ background: '#1A1A2E', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    data-testid="editor-content"
                    style={{ minHeight: '250px', color: '#EDEDED' }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="editor-preview">
              <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, background: 'rgba(217,251,6,0.15)', color: '#d9fb06', textTransform: 'uppercase', marginBottom: '8px' }}>
                {category}
              </span>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#EDEDED', marginBottom: '8px', lineHeight: 1.2 }}>
                {title}
              </h1>
              <p style={{ color: '#9CA3AF', fontSize: '0.9rem', marginBottom: '16px', fontStyle: 'italic' }}>{excerpt}</p>
              <div style={{ color: '#EDEDED', lineHeight: 1.7, fontSize: '0.95rem' }} dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          {msg && (
            <span style={{ fontSize: '0.8rem', color: msg.includes('berhasil') ? '#4ADE80' : '#F87171' }}>{msg}</span>
          )}
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.08)', color: '#A1A1AA', border: 'none', borderRadius: '8px',
              padding: '8px 18px', cursor: 'pointer', fontSize: '0.85rem',
            }}>Batal</button>
            <button data-testid="editor-save-btn" onClick={handleSave} disabled={saving} style={{
              background: '#d9fb06', color: '#0A0A0A', border: 'none', borderRadius: '8px',
              padding: '8px 18px', cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem',
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
              opacity: saving ? 0.6 : 1,
            }}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </div>
      </div>

      {/* Quill dark theme overrides */}
      <style>{`
        .quill-editor-wrapper .ql-toolbar { border-color: rgba(255,255,255,0.1) !important; background: rgba(255,255,255,0.03); }
        .quill-editor-wrapper .ql-container { border-color: rgba(255,255,255,0.1) !important; font-size: 0.95rem; min-height: 250px; }
        .quill-editor-wrapper .ql-editor { color: #EDEDED; min-height: 250px; }
        .quill-editor-wrapper .ql-editor.ql-blank::before { color: #71717A; font-style: italic; }
        .quill-editor-wrapper .ql-stroke { stroke: #A1A1AA !important; }
        .quill-editor-wrapper .ql-fill { fill: #A1A1AA !important; }
        .quill-editor-wrapper .ql-picker-label { color: #A1A1AA !important; }
        .quill-editor-wrapper .ql-picker-options { background: #1A1A2E !important; border-color: rgba(255,255,255,0.1) !important; }
        .quill-editor-wrapper .ql-picker-item { color: #EDEDED !important; }
        .quill-editor-wrapper .ql-active .ql-stroke { stroke: #d9fb06 !important; }
        .quill-editor-wrapper .ql-active .ql-fill { fill: #d9fb06 !important; }
        .quill-editor-wrapper .ql-active { color: #d9fb06 !important; }
        .quill-editor-wrapper .ql-editor h1, .quill-editor-wrapper .ql-editor h2, .quill-editor-wrapper .ql-editor h3 { color: #EDEDED; }
        .quill-editor-wrapper .ql-editor blockquote { border-left: 4px solid #d9fb06; color: #9CA3AF; padding-left: 12px; }
        .quill-editor-wrapper .ql-tooltip { background: #1A1A2E !important; color: #EDEDED !important; border-color: rgba(255,255,255,0.1) !important; }
        .quill-editor-wrapper .ql-tooltip input { color: #EDEDED !important; background: rgba(255,255,255,0.05) !important; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

const labelStyle = { display: 'block', color: '#9CA3AF', fontSize: '0.8rem', marginBottom: '4px', fontWeight: 500 };
const inputStyle = {
  width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px', padding: '8px 12px', color: '#EDEDED', fontSize: '0.9rem',
  boxSizing: 'border-box',
};
